import { resolveIcon } from '../../styles/icons'
import styled from "styled-components"
import { Text, Title, Tag } from "../generic/Core"
import useTranslation from "../../hooks/useTranslation"
import useLocale from "../../hooks/useLocale"
import DisplayElement from "./DisplayElement"
import DisplayLink from "./DisplayLink"
import Image from "../image/Image"
import RichTextDisplay from "../richText/RichTextDisplay"
import resolve from "../../../../common/utils/object/resolve"

const DisplayIcon = styled.span`
    margin-right: 0.5rem;
`

const Currency = styled.span`
    font-size: 0.8em;
`

/*
 * The label in front of a value — `element.prefix`, resolved.
 *
 * It used to be a bare `<span>`, so it ran straight into the value it
 * introduces: "Regnskabsdato27.9.2026", "ModpartJohn McCain". Seven older
 * displays hid that by baking ": " into the translation itself ("Invoice No. :
 * "), which is the same fix done twelve times per key, in the one place a
 * translator can get it wrong.
 *
 * The separation belongs to the renderer, and so does the contrast: muted and a
 * size down, so a column of label/value pairs reads as two columns rather than
 * one run-on line. The older keys keep their colon and still read correctly —
 * the gap is a margin, not a character, so nothing is duplicated.
 */
const Label = styled.span`
    margin-right: 0.4rem;
    color: ${({ theme }) => theme.font.label};
    font-size: 0.9em;
    font-weight: 400;
`

/*
 * The value itself, one step heavier than the label beside it. Not bold — a
 * display already has a bold title, and a whole column at 600 would flatten the
 * hierarchy it is meant to create.
 */
const Value = styled.span`
    font-weight: 500;
`

/*
 * Semantic status colours (theme.emotive), the same tokens StateBadge uses for
 * a record's state — 'info' by default, so a plain value-with-label pill reads
 * as informational rather than a warning/error unless told otherwise.
 */
const tone = ($variant, theme) => theme.emotive[$variant] || theme.emotive.info

const Pill = styled.span`
    display: inline-block;
    padding: 0.25rem 0.65rem;
    border-radius: 999px;
    font-size: var(--font-XS);
    font-weight: 500;
    white-space: nowrap;
    background-color: ${({ $variant, theme }) => tone($variant, theme).bg};
    color: ${({ $variant, theme }) => tone($variant, theme).color};
    border: 1px solid ${({ $variant, theme }) => tone($variant, theme).border};
`

const MergedText = ({ displayData, element }) => {
    const { m, t } = useTranslation()
    const { lf } = useLocale()
    const params = element?.mergeParams?.map(param => {
        if (typeof param === 'string') {
            const val = resolve(param, displayData)
            if (val === null || val === undefined) return ''
            return typeof val === 'number' ? val.toString() : val
        }
        if (param?.type === 'label') {
            return t(param.value)
        }
        // A translation key held in a field, for rows that name themselves by
        // key — a number series row carries 'nserie.entity.invoice'.
        if (param?.type === 'translate') {
            const key = resolve(param.value, displayData)
            return key ? t(key) : ''
        }
        /*
         * A number the reader sees as money: 1000 becomes 1.000,00 in Danish and
         * 1,000.00 in English. A plain string param renders the raw number, which
         * is right for a count or a percentage and wrong for an amount.
         */
        if (param?.type === 'amount') {
            return lf(resolve(param.value, displayData) ?? 0, 2, 2)
        }
        if (param?.type === 'boolean') {
            const val = resolve(param.value, displayData)
            return t(val ? param.values[0] : param.values[1])
        }
        return ''
    }) || []
    return element && <span>{m(element.value, params)}</span>
}

const DisplayElementField = ({ displayData, element, currency }) => {
    const { t } = useTranslation()
    const { lf } = useLocale()
    const fieldValue = resolve(element.value, displayData)

    return (
        <>
            <span>
                {element.icon && <DisplayIcon>{resolveIcon(element.icon)}</DisplayIcon>}
            </span>
            {element.prefix && <Label>{t(element.prefix)}</Label>}
            {element.type === 'currency' && <Currency>{currency}</Currency>}
            {element.type === 'field' && <Value>{fieldValue}</Value>}
            {element.type === 'text' && <span>{t(element.value)}</span>}
            {/* A state, a kind — a short word that classifies the record rather
                than measuring it. `pill` was numeric only, so the alternative was
                plain text that read as one more label. */}
            {element.type === 'statusPill' && <Pill $variant={element.variant || 'info'}>{t(element.value)}</Pill>}
            {element.type === 'amount' && <>
                <DisplayElement type='amount' item={displayData} col={element} string={fieldValue} />
                {/* 'currency' keeps reading the section's shared currency prop (unchanged
                    behavior for every existing config); any other decorator is a field
                    id resolved against this row's own data — same convention listViewData's
                    TableDecorator already uses (`item[col.decorator]`). */}
                {element.decorator === 'currency' && <Currency>{` ${currency}`}</Currency>}
                {element.decorator && element.decorator !== 'currency' && <Currency>{` ${resolve(element.decorator, displayData) ?? ''}`}</Currency>}
            </>}
            {element.type === 'pill' && <Pill $variant={element.variant || 'info'}>
                {/* Always shown, zero when unset — a self-balancing line legitimately
                    carries 0 on one side until it expands into two legs at posting,
                    which is not the same thing as missing data. */}
                {`${t(element.label)}: ${lf(fieldValue ?? 0, 2, 2)}`}
                {element.decorator && ` ${resolve(element.decorator, displayData) ?? ''}`}
            </Pill>}
            {element.type === 'dualPill' && (() => {
                // One pill, not two: a debit/credit pair is mutually exclusive by
                // design, so whichever side is actually set decides the label —
                // debit wins a genuine tie (both zero, e.g. a still-blank line).
                const debitValue = resolve(element.debitField, displayData) ?? 0
                const creditValue = resolve(element.creditField, displayData) ?? 0
                const isCredit = creditValue !== 0 && debitValue === 0
                const label = isCredit ? element.creditLabel : element.debitLabel
                const amount = isCredit ? creditValue : debitValue
                return (
                    <Pill $variant={element.variant || 'info'}>
                        {`${t(label)}: ${lf(amount, 2, 2)}`}
                        {element.decorator && ` ${resolve(element.decorator, displayData) ?? ''}`}
                    </Pill>
                )
            })()}
            {element.type === 'merge' && <MergedText displayData={displayData} element={element} />}
            {/* Spelled 'docLink', not 'link': a table column's `link` is an
                external href opened in a new tab (DisplayElement), and this one
                navigates inside the app. One word meaning two things is how a
                config silently renders the wrong thing. */}
            {element.type === 'docLink' && <DisplayLink displayData={displayData} element={element} />}
            {element.type === 'date' && <Value><DisplayElement type='date' string={fieldValue} /></Value>}
            {/* The date AND the time. formatString has handled it all along;
                nothing in a display could ask for it, so a posting time could
                only be shown to the day — which is not what it records. */}
            {element.type === 'datetime' && <Value><DisplayElement type='datetime' string={fieldValue} /></Value>}
            {element.type === 'niceDate' && <Value><DisplayElement type='niceDate' string={fieldValue} /></Value>}
            {element.type === 'image' && <Image path={fieldValue} width={element.width} height={element.height} />}
            {element.type === 'arrayField' && <Value>{
                Array.isArray(fieldValue)
                    ? fieldValue.map(item => item[element.displayField]).filter(Boolean).join(element.separator || ', ')
                    : ''
            }</Value>}
        </>
    )
}

const DisplayElementType = ({ displayData, element, $variant, currency }) => {
    if (element.type === 'richtext') {
        const fieldValue = resolve(element.value, displayData)
        return <RichTextDisplay value={fieldValue} maxLines={element.maxLines} />
    }
    if (element.type === 'spacer') {
        if (element.scale === 'title') return <div><Title $variant={$variant}>&nbsp;</Title></div>
        if (element.scale === 'normal') return <div><Text $variant={$variant}>&nbsp;</Text></div>
        return <div>&nbsp;</div>
    }
    if (element.scale === 'title') return (
        <div>
            <Title $variant={$variant}>
                <DisplayElementField displayData={displayData} element={element} currency={currency} />
                {element.tag && <span><Tag $variant='primary'>{resolve(element.tag, displayData)}</Tag></span>}
            </Title>
        </div>
    )
    if (element.scale === 'normal') return (
        <div>
            <Text $variant={$variant}>
                <DisplayElementField displayData={displayData} element={element} currency={currency} />
            </Text>
        </div>
    )
    if (element.scale === 'other') return (
        <div>
            <DisplayElementField displayData={displayData} element={element} currency={currency} />
        </div>
    )
    console.warn(`DisplayElementType: unknown element.scale "${element.scale}"`)
    return null
}

export default DisplayElementType
